/**
 * OnboardingTenantCard Component
 * Per-tenant candidate transaction selection during onboarding flow
 */

import type { OnboardingTenant, CandidateTransaction } from '../api/external-payments.api';
import { formatLocalDate } from '../utils/date';
import { formatUnitLabel } from '@resida/shared';

interface OnboardingTenantCardProps {
  tenant: OnboardingTenant;
  selectedTransactionIds: Set<string>;
  skipped: boolean;
  onTransactionToggle: (txId: string) => void;
  onSkip: () => void;
}

export function OnboardingTenantCard({
  tenant,
  selectedTransactionIds,
  skipped,
  onTransactionToggle,
  onSkip,
}: OnboardingTenantCardProps): JSX.Element {
  const formattedRent = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(tenant.rentAmount / 100);

  return (
    <div
      data-testid={`onboarding-tenant-card-${tenant.tenantId}`}
      className={`bg-card rounded-lg p-5 border mb-4 ${skipped ? 'opacity-50' : 'border-border'}`}
    >
      <div className="flex justify-between items-start mb-4">
        <div>
          <h3 className="text-base font-semibold text-foreground">
            {tenant.firstName} {tenant.lastName}
          </h3>
          <p className="text-sm text-muted-foreground mt-0.5">
            {formatUnitLabel(tenant.unitNumber) ? `${formatUnitLabel(tenant.unitNumber)} · ` : ''}Expected rent: {formattedRent}
          </p>
        </div>
        {!skipped && (
          <button
            type="button"
            data-testid={`skip-tenant-${tenant.tenantId}`}
            onClick={onSkip}
            className="px-3 py-1.5 text-xs font-medium text-muted-foreground border border-border rounded hover:bg-accent min-h-[36px]"
          >
            Skip
          </button>
        )}
        {skipped && (
          <span className="text-xs text-muted-foreground italic">Skipped</span>
        )}
      </div>

      {skipped ? null : tenant.candidateTransactions.length === 0 ? (
        <p className="text-sm text-muted-foreground italic">No candidate transactions found.</p>
      ) : (
        <div className="space-y-2">
          <p className="text-xs font-medium text-muted-foreground mb-2">Select matching transactions:</p>
          {tenant.candidateTransactions.map((tx: CandidateTransaction) => (
            <CandidateTransactionRow
              key={tx.id}
              transaction={tx}
              isSelected={selectedTransactionIds.has(tx.id)}
              onToggle={() => onTransactionToggle(tx.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

interface CandidateTransactionRowProps {
  transaction: CandidateTransaction;
  isSelected: boolean;
  onToggle: () => void;
}

function CandidateTransactionRow({
  transaction,
  isSelected,
  onToggle,
}: CandidateTransactionRowProps): JSX.Element {
  const formattedDate = formatLocalDate(transaction.date);
  const formattedAmount = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(transaction.amount);

  return (
    <label
      data-testid={`candidate-transaction-${transaction.id}`}
      className={`flex items-start gap-3 p-3 rounded border cursor-pointer transition-colors ${
        isSelected ? 'border-primary bg-primary/5' : 'border-border hover:bg-accent'
      }`}
    >
      <input
        type="checkbox"
        data-testid={`candidate-transaction-checkbox-${transaction.id}`}
        checked={isSelected}
        onChange={onToggle}
        className="mt-0.5 h-4 w-4 text-primary border-input rounded"
      />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-foreground truncate">{transaction.description}</p>
        <p className="text-xs text-muted-foreground mt-0.5">{formattedDate}</p>
      </div>
      <span className="text-sm font-semibold text-foreground shrink-0">{formattedAmount}</span>
    </label>
  );
}
